// src/components/AuthShell.jsx
// 3.40.0: one look for every screen before the portal opens: sign in,
// Forgot password, the two-factor code and its "lost access" screens,
// setting up two-factor, choosing a password, and the pages the emailed
// links open (set password, reset two-factor, request access, confirm email).
//
//   <AuthShell>…</AuthShell>   the animated background and the glass card;
//                              on wide screens a welcome panel sits beside it
//   <AuthMessage tone>         error / success / info messages with an icon
//                              (errors give a small shake)
//   <PasswordField>            a password box with Show / Hide and a Caps
//                              Lock warning
//   <AuthButton busy>          the main button, with a spinner while busy
//   A                          shared styles (title, text, label, input, link…)
//
// Only the look changed: every screen keeps its own sign-in logic. Motion is
// CSS only (transforms and opacity), and it all stops for people who ask
// their computer for less motion.
import { useEffect, useRef, useState } from 'react';
import Footer from './Footer';
import Emo from './Emo';
import StatusIcon from './StatusIcon';
import { LOGO_URL } from '../utils/brand';
import { API_BASE } from '../firebase/config';

// 3.40.1: picked at random each time the page opens (3.43.2: plus the
// compliance one, always shown: our selling point; 3.48.10: at a random
// place, not always last; 3.63.0: two plus compliance)
const FEATURES = [
  ['📠', 'Send and receive faxes right from your browser'],
  ['✉️', 'Send a fax just by sending an email'],
  ['📥', 'Faxes you receive arrive in your email inbox too'],
  ['⚡', 'Watch each fax go out live, from queued to delivered'],
  ['🔁', 'Failed faxes are retried for you automatically'],
  ['🔔', 'Pop-ups tell you the moment a fax arrives or is delivered'],
  ['🕒', 'Schedule a fax to go out later'],
  ['📝', 'Cover sheets built in, ready in seconds'],
  ['📇', 'Shared contacts with suggested caller names'],
  ['👥', 'Teams with their own fax numbers'],
  ['🛡️', 'Roles and permissions for every person'],
  ['🔐', 'Two-factor sign-in keeps your faxes safe'],
  ['🔍', 'Search, filter and download reports to Excel'],
  ['📜', 'A full activity log of who did what'],
  ['📚', 'How-to articles and videos whenever you need them'],
  // 3.43.2
  ['⭐', 'Priority numbers get extra tries when a fax fails'],
  ['🚫', 'Block numbers your team should never fax'],
  ['🗑️', 'Deleted faxes wait in Trash, so nothing is lost by mistake'],
  ['👁️', 'See who opened each fax you receive'],
  ['🩺', 'A daily test fax and live health checks watch the service for you'],
];
const COMPLIANCE = ['🛡️', 'Security & compliance reports, ready for your audits'];
// 3.63.0 (owner): every feature in turn, compliance included, in a random
// order each visit; three at a time, the next three every few seconds
const shuffled = () => {
  const list = [...FEATURES, COMPLIANCE];
  for (let i = list.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [list[i], list[j]] = [list[j], list[i]]; }
  return list;
};
const STEP_MS = 1500;   // 3.63.0 (owner): one line changes at a time; each stays ~4.5 s
const FADE_MS = 520;

// stack: the card's parts sit in a column with even spacing (the link pages)
// 3.63.0 (owner): the dark brand panel from the owner's Claude Design
// sign-in mock-up, made from our own parts: our logo and font, a made-up fax
// going out, three of our features, a status line from Health. Phones get
// the headline, the card and the status line, in that order.
export default function AuthShell({ children, wide = false, viewKey = '', brand = true, stack = false }) {
  const rootRef = useRef(null);
  // The background drifts a little with the mouse (one update per frame)
  useEffect(() => {
    const el = rootRef.current;
    if (!el || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0, x = 0, y = 0;
    const move = e => {
      x = e.clientX / window.innerWidth - 0.5;
      y = e.clientY / window.innerHeight - 0.5;
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; el.style.setProperty('--mx', x.toFixed(3)); el.style.setProperty('--my', y.toFixed(3)); });
    };
    window.addEventListener('pointermove', move, { passive: true });
    return () => { window.removeEventListener('pointermove', move); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div ref={rootRef} className="auth-root">
      <style>{CSS}</style>
      <div className="auth-bg" aria-hidden="true">
        <span className="auth-blob b1" /><span className="auth-blob b2" />
        <span className="auth-grid" /><span className="auth-scan" />
      </div>
      <div className={`auth-layout${brand ? '' : ' solo'}`}>
        {brand && (
          <aside className="auth-brand">
            <div className="auth-brand-top">
              <span className="auth-logo-plate"><img src={LOGO_URL} alt="Crazy Pixels" /></span>
              <span className="auth-eyebrow">Fax Portal</span>
            </div>
            <div className="auth-hero">
              <h2 className="auth-headline">Faxing, minus the fax machine.</h2>
              <p className="auth-lede"><strong>Simple, secure and online.</strong> Send, receive and track every fax from your browser, or straight from your email.</p>
              <LiveFax />
              <Features />
            </div>
            <StatusLine />
          </aside>
        )}
        {/* a div, not <main>: the portal's own button styles are for its pages */}
        <div role="main" className={`auth-card${wide ? ' wide' : ''}`}>
          {/* 3.51.3: shown on phones, tablets and pages without the side panel: the logo with "Fax Portal" in it */}
          {/* 3.63.0 (owner, Oct 22): like the side panel, the logo with "Fax Portal" beside it on one line */}
          <div className="auth-card-logo"><img src={LOGO_URL} alt="Crazy Pixels" /><span className="auth-card-name">Fax Portal</span></div>
          {/* 3.63.0 (owner, Oct 22): on phones the tagline sits under that logo, with the shine */}
          {brand && <p className="auth-card-tagline">Simple, secure and online.</p>}
          <div key={viewKey} className={`auth-view${stack ? ' stack' : ''}`}>{children}</div>
        </div>
      </div>
      <div className="auth-footer"><Footer showVersion /></div>
    </div>
  );
}

// A made-up fax going out (dummy number and file, as in training), with the
// same steps as the Outbox's progress bar (components/FaxProgress): Queued,
// Preparing, Sending with a percentage, then Delivered. Still for people who
// ask for less motion.
const STEPS = [['Queued', 12], ['Preparing', 32], ['Sending', 52], ['Sending', 68], ['Sending', 83], ['Sending', 93], ['Delivered', 100], ['Delivered', 100]];
function LiveFax() {
  const [i, setI] = useState(4);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    setI(0);
    const t = setInterval(() => setI(n => (n + 1) % STEPS.length), 1500);
    return () => clearInterval(t);
  }, []);
  const [label, pct] = STEPS[i];
  const done = label === 'Delivered';
  return (
    <div className={`auth-live${done ? ' done' : ''}`} aria-hidden="true">
      <div className="auth-live-row">
        <span className="auth-live-state"><span className="auth-live-dot" />{done ? 'Delivered' : `${label}…`}</span>
        <span className="auth-mono">{done ? '' : `${pct}%`}</span>
      </div>
      <div className="auth-live-bar"><span style={{ width: `${pct}%` }} /></div>
      <div className="auth-live-row auth-mono">
        <span>+1 (555) 010-0142 · 4 pages</span><span>referral-letter.pdf</span>
      </div>
    </div>
  );
}

// Three features at a time, changing one by one (owner): every 1.5 seconds
// the next line fades out (a soft lift) and a new feature fades in, so each
// line stays about 4.5 seconds. Waits while the pointer is over them; stays
// put for people who ask for less motion.
function Features() {
  const [list] = useState(shuffled);
  const [slots, setSlots] = useState([0, 1, 2]);           // which feature each line shows
  const [phase, setPhase] = useState(['pre', 'pre', 'pre']);   // pre (hidden, below) → in → out (hidden, above)
  const hold = useRef(false);
  const [intro, setIntro] = useState(true);   // the first three come in one after another
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const setOne = (k, v) => setPhase(p => p.map((x, i) => (i === k ? v : x)));
    const show = k => requestAnimationFrame(() => requestAnimationFrame(() => (k === null ? setPhase(['in', 'in', 'in']) : setOne(k, 'in'))));
    show(null);
    if (reduce) return undefined;
    let turn = 0, next = 3, swap = 0;
    const t = setInterval(() => {
      if (hold.current || document.hidden) return;
      const k = turn++ % 3;
      setIntro(false);
      setOne(k, 'out');
      swap = setTimeout(() => {
        const n = next; next = (next + 1) % list.length;
        setSlots(sl => sl.map((x, i) => (i === k ? n : x)));
        setOne(k, 'pre');
        show(k);
      }, FADE_MS + 120);
    }, STEP_MS);
    return () => { clearInterval(t); clearTimeout(swap); };
  }, [list.length]);
  return (
    <ul className="auth-features" onPointerEnter={() => { hold.current = true; }} onPointerLeave={() => { hold.current = false; }}>
      {slots.map((n, k) => {
        const [icon, text] = list[n];
        return (
          <li key={k} className={phase[k]} style={{ transitionDelay: intro ? `${k * 110}ms` : '0ms' }}>
            <span className="auth-feat-icon" aria-hidden="true"><Emo e={icon} /></span>{text}
          </li>
        );
      })}
    </ul>
  );
}

// The same plain words clients see on the Overview (lib/systemStatus);
// nothing shown if it can't be read
function StatusLine() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let live = true;
    const read = () => fetch(`${API_BASE}/portal/access/status`).then(r => (r.ok ? r.json() : null))
      .then(j => { if (live && j?.level) setSt(j); }).catch(() => {});
    read();
    const t = setInterval(read, 2 * 60 * 1000);   // the server keeps the result 2 minutes too
    return () => { live = false; clearInterval(t); };
  }, []);
  // 3.63.0 (owner): the mock-up's wording; when Health isn't green, its own plain message
  return (
    <ul className="auth-strip">
      {/* 3.68.9 (owner): the animated status icon, as on the Overview and Health */}
      {st && <li className={`auth-status ${st.level}`}><StatusIcon level={st.level} size={16} />{st.level === 'green' ? 'All systems operational' : st.message}</li>}
      <li className="auth-strip-extra"><Emo e="🔑" /> TLS-encrypted in transit</li>
      <li className="auth-strip-extra"><Emo e="📜" /> Audit log on every fax</li>
    </ul>
  );
}

const ICON = { error: '!', success: '✓', info: 'i' };

export function AuthMessage({ tone = 'error', id, children }) {
  if (!children) return null;
  return (
    <div id={id} role={tone === 'error' ? 'alert' : 'status'} className={`auth-msg ${tone}`}>
      <span className="auth-msg-icon" aria-hidden="true">{ICON[tone] || 'i'}</span>
      <div className="auth-msg-text">{children}</div>
    </div>
  );
}

export function PasswordField({ inputRef, style, ...props }) {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const checkCaps = e => setCaps(!!e.getModifierState?.('CapsLock'));
  return (
    <div className="auth-pw">
      <input {...props} ref={inputRef} type={show ? 'text' : 'password'} style={{ ...A.input, paddingRight: 64, ...style }}
        onKeyUp={checkCaps} onKeyDown={checkCaps} onBlur={e => { setCaps(false); props.onBlur?.(e); }} />
      <button type="button" className="auth-pw-toggle" onClick={() => setShow(s => !s)}
        aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>{show ? 'Hide' : 'Show'}</button>
      {caps && <span className="auth-caps" role="status">⇪ Caps Lock is on</span>}
    </div>
  );
}

export function AuthButton({ busy, busyLabel, children, ...props }) {
  return (
    <button type="submit" {...props} disabled={busy || props.disabled} className={`auth-btn${busy ? ' busy' : ''}`} style={props.style}>
      {busy && <span className="auth-spin" aria-hidden="true" />}
      <span>{busy ? (busyLabel || 'Please wait…') : children}</span>
    </button>
  );
}

export const A = {
  title: { textAlign: 'center', margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: '#004D40', letterSpacing: '-0.4px', lineHeight: 1.2 },
  subtitle: { textAlign: 'center', margin: '0 0 22px', fontSize: 14.5, color: '#607D8B', lineHeight: 1.5 },
  text: { margin: '0 0 14px', fontSize: 14, color: '#455A64', lineHeight: 1.6 },
  form: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12.5, fontWeight: 700, color: '#546E7A', marginBottom: 2, letterSpacing: '0.2px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #dfe7e6', background: 'rgba(255,255,255,0.9)',
    fontSize: 15, color: '#263238', marginBottom: 12, outline: 'none', fontFamily: 'inherit' },
  inputError: { border: '1.5px solid #e53935', background: '#fff8f8', boxShadow: '0 0 0 4px rgba(229,57,53,0.12)' },
  link: { background: 'none', border: 'none', color: '#00897B', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, padding: 0, fontFamily: 'inherit' },
  links: { textAlign: 'center', marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
  linkSep: { color: '#CFD8DC', fontSize: 13 },
  smallNote: { textAlign: 'center', fontSize: 12.5, color: '#78909C', margin: '12px 0 0', lineHeight: 1.5 },
  footerRow: { marginTop: 20, paddingTop: 14, borderTop: '1px solid rgba(0,105,92,0.10)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  signedInAs: { fontSize: 12, color: '#78909C', overflowWrap: 'anywhere' },
};

const CSS = `
.auth-root { --mx: 0; --my: 0; position: relative; min-height: 100vh; display: flex; flex-direction: column; overflow: hidden;
  font-family: 'DM Sans', 'Segoe UI', sans-serif; background: #032a25; color: #e6f4f1; isolation: isolate; }
/* 3.63.0: deep teal, two slow glows, a faint grid in perspective and a scan line, like a page going through */
.auth-bg { position: fixed; inset: 0; z-index: -1; overflow: hidden; background: linear-gradient(170deg, #05392f 0%, #032a25 55%, #021b18 100%); }
.auth-blob { position: absolute; border-radius: 50%; filter: blur(60px); will-change: transform; }
.auth-blob.b1 { width: 60vmax; height: 60vmax; left: -18vmax; top: -24vmax; opacity: .5; background: radial-gradient(circle, rgba(38,166,154,.7), transparent 62%);
  transform: translate(calc(var(--mx) * -30px), calc(var(--my) * -30px)); animation: authFloat1 24s ease-in-out infinite; }
.auth-blob.b2 { width: 52vmax; height: 52vmax; right: -16vmax; bottom: -24vmax; opacity: .45; background: radial-gradient(circle, rgba(30,136,229,.55), transparent 64%);
  transform: translate(calc(var(--mx) * 40px), calc(var(--my) * 40px)); animation: authFloat2 30s ease-in-out infinite; }
@keyframes authFloat1 { 0%, 100% { translate: 0 0; } 50% { translate: 4vmax 3vmax; } }
@keyframes authFloat2 { 0%, 100% { translate: 0 0; } 50% { translate: -5vmax -3vmax; } }
.auth-grid { position: absolute; inset: 0; transform: perspective(900px) rotateX(18deg) scale(1.15); transform-origin: 50% 100%;
  background-image: linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px); background-size: 56px 56px;
  mask-image: radial-gradient(ellipse 70% 80% at 28% 45%, #000 20%, transparent 70%); -webkit-mask-image: radial-gradient(ellipse 70% 80% at 28% 45%, #000 20%, transparent 70%); }
.auth-scan { position: absolute; left: 0; right: 0; top: 0; height: 1px; opacity: 0; background: linear-gradient(90deg, transparent 10%, rgba(128,203,196,.7), transparent 90%);
  box-shadow: 0 0 36px 5px rgba(128,203,196,.22); animation: authScan 10s linear infinite; }
@keyframes authScan { 0% { transform: translateY(0); opacity: 0; } 10%, 90% { opacity: 1; } 100% { transform: translateY(100vh); opacity: 0; } }

.auth-layout { flex: 1; display: grid; grid-template-columns: minmax(0, 600px) minmax(0, 440px); gap: clamp(32px, 6vw, 100px);
  align-items: center; justify-content: center; padding: 48px 24px 8px; }
.auth-layout.solo { grid-template-columns: minmax(0, 600px); }
.auth-brand { display: flex; flex-direction: column; gap: 34px; animation: authRise .7s cubic-bezier(.2,.8,.2,1) both; }
.auth-brand-top { display: flex; align-items: center; gap: 14px; }
.auth-logo-plate { display: inline-flex; align-items: center; padding: 9px 14px; border-radius: 14px; background: rgba(255,255,255,.95);
  box-shadow: 0 10px 28px rgba(0,0,0,.28); }
.auth-logo-plate img { height: 30px; width: auto; display: block; }
/* 3.63.0 (owner, Oct 22): "Fax Portal" in the earlier page's style (extra bold, slow shine), lighter shades for the dark panel */
.auth-eyebrow { font-size: clamp(30px, 3vw, 38px); font-weight: 800; letter-spacing: -1px; line-height: 1.05;
  background: linear-gradient(110deg, #E0F2F1, #4DB6AC 40%, #A5D6A7 60%, #E0F2F1); background-size: 250% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: authShine 8s ease-in-out infinite; }
@keyframes authShine { 0%, 100% { background-position: 0% 0; } 50% { background-position: 100% 0; } }
.auth-hero { display: flex; flex-direction: column; gap: 22px; }
.auth-headline { margin: 0; font-size: clamp(36px, 4.2vw, 54px); line-height: 1.03; letter-spacing: -1.6px; font-weight: 800; color: #fff; text-wrap: balance; }
.auth-lede strong { color: #fff; font-weight: 700; }
.auth-lede { margin: 0; font-size: 17px; line-height: 1.55; color: #b2d8d2; max-width: 440px; }
.auth-mono { font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-size: 12px; color: #a7d3cb; }
.auth-live { display: flex; flex-direction: column; gap: 12px; max-width: 420px; padding: 16px 18px; border-radius: 16px;
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
.auth-live-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.auth-live-state { display: inline-flex; align-items: center; gap: 9px; font-size: 13.5px; font-weight: 700; color: #fff; }
.auth-live-dot { width: 8px; height: 8px; border-radius: 50%; background: #4DD0E1; box-shadow: 0 0 10px #4DD0E1; animation: authPulse 1.4s ease-in-out infinite; }
.auth-live.done .auth-live-dot { background: #66BB6A; box-shadow: 0 0 10px #66BB6A; animation: none; }
.auth-live-bar { height: 6px; border-radius: 99px; background: rgba(255,255,255,.1); overflow: hidden; }
.auth-live-bar span { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, #26A69A, #4DD0E1); transition: width .9s cubic-bezier(.4,0,.2,1); }
.auth-live.done .auth-live-bar span { background: linear-gradient(90deg, #43A047, #66BB6A); }
@keyframes authPulse { 0%, 100% { opacity: .45; } 50% { opacity: 1; } }
.auth-features { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; min-height: 116px; }
.auth-features li { display: flex; align-items: center; gap: 12px; font-size: 15px; color: #d7ece8; font-weight: 500;
  transition: opacity .52s ease, transform .52s cubic-bezier(.2,.8,.2,1), filter .52s ease; }
.auth-features li.pre { opacity: 0; transform: translateY(10px); filter: blur(3px); transition: none; }
.auth-features li.out { opacity: 0; transform: translateY(-8px); filter: blur(3px); }
.auth-feat-icon { width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center; font-size: 16px; flex-shrink: 0;
  background: rgba(255,255,255,.08); box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); }
/* 3.63.0 (owner): one line, set apart from the features by a thin rule */
.auth-strip { list-style: none; margin: 0; padding: 16px 0 0; border-top: 1px solid rgba(255,255,255,.12); display: flex; flex-wrap: nowrap; gap: 6px 14px;
  font-size: 12px; color: #a7d3cb; white-space: nowrap; }
.auth-strip li { display: inline-flex; align-items: center; gap: 6px; }
.auth-strip li > :first-child { font-size: 14px; }
@media (max-width: 1180px) { .auth-strip { flex-wrap: wrap; white-space: normal; } }
.auth-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #66BB6A; box-shadow: 0 0 8px rgba(102,187,106,.7); }
.auth-status.amber .auth-status-dot { background: #FFB74D; box-shadow: 0 0 8px rgba(255,183,77,.7); }
.auth-status.red .auth-status-dot { background: #EF5350; box-shadow: 0 0 8px rgba(239,83,80,.7); }

.auth-card { position: relative; width: 100%; box-sizing: border-box; padding: 36px 34px 28px; border-radius: 22px; color: #263238;
  background: #fff; box-shadow: 0 30px 80px rgba(0,18,15,.5), 0 2px 6px rgba(0,0,0,.08);
  animation: authCardIn .7s cubic-bezier(.2,.8,.2,1) both; }
.auth-card.wide { max-width: 600px; justify-self: center; }
.auth-card-logo { display: none; align-items: center; justify-content: center; flex-wrap: wrap; gap: 6px 12px; margin-bottom: 16px; }
.auth-card-logo img { height: 34px; width: auto; display: block; }
.auth-card-name { font-size: 26px; font-weight: 800; letter-spacing: -.8px; line-height: 1.05;
  background: linear-gradient(110deg, #004D40, #00897B 40%, #43A047 60%, #004D40); background-size: 250% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: authShine 8s ease-in-out infinite; }
.auth-layout.solo .auth-card-logo { display: flex; }
.auth-card-tagline { display: none; margin: -6px 0 22px; padding-bottom: 18px; border-bottom: 1px solid #E0F2F1; text-align: center;
  font-size: 19px; line-height: 1.2; letter-spacing: -.4px; font-weight: 800; text-wrap: balance;
  background: linear-gradient(110deg, #004D40, #00897B 40%, #43A047 60%, #004D40); background-size: 250% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: authShine 8s ease-in-out infinite; }
.auth-view.stack { display: flex; flex-direction: column; gap: 14px; }
.auth-view.stack input { margin-bottom: 0 !important; }
.auth-view.stack .auth-caps { margin: 6px 0 0; }
.auth-view { animation: authView .45s cubic-bezier(.2,.8,.2,1) both; }
@keyframes authCardIn { from { opacity: 0; transform: translateY(26px) scale(.97); } to { opacity: 1; transform: none; } }
@keyframes authRise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
@keyframes authSlide { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: none; } }
@keyframes authView { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: none; } }

.auth-root input:focus, .auth-root select:focus, .auth-root textarea:focus { border-color: #00897B !important; box-shadow: 0 0 0 4px rgba(0,137,123,0.15) !important; background: #fff !important; }
.auth-root input { transition: border-color .2s, box-shadow .2s, background .2s; }
.auth-root input[aria-invalid="true"], .auth-root input[aria-invalid="true"]:focus { border-color: #e53935 !important; box-shadow: 0 0 0 4px rgba(229,57,53,0.14) !important; background: #fff8f8 !important; }
.auth-btn { position: relative; overflow: hidden; display: inline-flex; align-items: center; justify-content: center; gap: 10px; width: 100%;
  padding: 13px 16px; margin-top: 6px; border: none; border-radius: 12px; cursor: pointer; font-family: inherit; font-size: 15.5px; font-weight: 700; color: #fff;
  background: linear-gradient(135deg, #00897B, #26A69A 55%, #43A047); background-size: 160% 160%; box-shadow: 0 8px 20px rgba(0,137,123,0.32);
  transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s, background-position .4s, filter .2s; }
.auth-btn::after { content: ''; position: absolute; top: 0; bottom: 0; width: 40%; left: -60%; transform: skewX(-20deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent); transition: left .6s ease; }
.auth-btn:hover:not(:disabled) { transform: scale(1.02); box-shadow: 0 12px 26px rgba(0,137,123,0.38); background-position: 100% 0; }
.auth-btn:hover:not(:disabled)::after { left: 120%; }
.auth-btn:active:not(:disabled) { filter: brightness(.94); }
.auth-btn:disabled { cursor: default; filter: saturate(.7); opacity: .85; }
.auth-spin { width: 16px; height: 16px; border-radius: 50%; border: 2.5px solid rgba(255,255,255,0.4); border-top-color: #fff; animation: spin .7s linear infinite; }
.auth-google { width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 12px 16px; border-radius: 12px;
  border: 1.5px solid #dfe7e6; background: #fff; cursor: pointer; font-size: 15px; font-weight: 600; color: #263238; font-family: inherit; margin-bottom: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04); transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s, border-color .2s; }
.auth-google:hover:not(:disabled) { transform: scale(1.02); border-color: #b2dfdb; box-shadow: 0 10px 22px rgba(0,77,64,0.12); }
.auth-google:active:not(:disabled) { filter: brightness(.96); }
.auth-google svg { transition: transform .4s cubic-bezier(.34,1.5,.64,1); }
.auth-google:hover:not(:disabled) svg { transform: rotate(360deg); }
.auth-divider { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; color: #90A4AE; font-size: 12.5px; font-weight: 600; }
.auth-divider::before, .auth-divider::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, #cfd8dc, transparent); }
.auth-root .auth-link { position: relative; }
.auth-root .auth-link::after { content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 1.5px; background: currentColor; transform: scaleX(0); transition: transform .25s ease; transform-origin: left; }
.auth-root .auth-link:hover::after { transform: scaleX(1); }
.auth-option { transition: transform .2s cubic-bezier(.34,1.5,.64,1), border-color .2s, box-shadow .2s, background .2s; }
.auth-option:hover:not(:disabled) { transform: scale(1.02); border-color: #80cbc4 !important; box-shadow: 0 10px 22px rgba(0,77,64,0.10); background: #fbfffe !important; }

/* the two-factor setup's own buttons (components/TwoFactorSetup) take this look on these screens */
.auth-root .tfa-primary { background: linear-gradient(135deg, #00897B, #26A69A 55%, #43A047) !important; border: none !important; border-radius: 12px !important;
  padding: 11px 20px !important; font-weight: 700 !important; box-shadow: 0 8px 20px rgba(0,137,123,0.28);
  transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s; }
.auth-root .tfa-primary:hover:not(:disabled) { transform: scale(1.02); box-shadow: 0 12px 26px rgba(0,137,123,0.36); }
.auth-root .tfa-secondary { border-radius: 12px !important; transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s; }
.auth-root .tfa-secondary:hover:not(:disabled) { transform: scale(1.02); box-shadow: 0 8px 18px rgba(0,77,64,0.12); }
.auth-pw { position: relative; }
.auth-pw-toggle { position: absolute; right: 8px; top: 7px; padding: 6px 10px; border: none; border-radius: 8px; background: #f0faf8; color: #00796B;
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; transition: background .2s; }
.auth-pw-toggle:hover { background: #e0f2f1; }
.auth-caps { display: block; margin: -6px 0 10px; font-size: 12px; font-weight: 600; color: #ef6c00; animation: authView .25s ease both; }

.auth-msg { display: flex; gap: 10px; align-items: flex-start; padding: 11px 14px; border-radius: 12px; font-size: 13.5px; line-height: 1.5; margin: 2px 0 10px;
  border: 1px solid transparent; animation: authMsgIn .35s cubic-bezier(.2,.8,.2,1) both; }
.auth-msg.error { background: linear-gradient(135deg, #fff5f5, #ffebee); border-color: #ffcdd2; color: #b71c1c; animation: authMsgIn .35s cubic-bezier(.2,.8,.2,1) both, authShake .45s .1s ease both; }
.auth-msg.success { background: linear-gradient(135deg, #f1fbf3, #e8f5e9); border-color: #c8e6c9; color: #1b5e20; }
.auth-msg.info { background: linear-gradient(135deg, #f3f9ff, #e3f2fd); border-color: #bbdefb; color: #0d47a1; }
.auth-msg-icon { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; font-size: 12.5px; font-weight: 800; color: #fff; margin-top: 0; }
.auth-msg.error .auth-msg-icon { background: #e53935; box-shadow: 0 0 0 4px rgba(229,57,53,0.15); }
.auth-msg.success .auth-msg-icon { background: #43A047; box-shadow: 0 0 0 4px rgba(67,160,71,0.15); animation: authPop .4s .1s cubic-bezier(.34,1.6,.64,1) both; }
.auth-msg.info .auth-msg-icon { background: #1E88E5; box-shadow: 0 0 0 4px rgba(30,136,229,0.15); font-family: Georgia, serif; font-style: italic; }
.auth-msg-text { flex: 1; min-width: 0; padding-top: 1px; }
@keyframes authMsgIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@keyframes authShake { 0%, 100% { translate: 0 0; } 20% { translate: -6px 0; } 40% { translate: 5px 0; } 60% { translate: -3px 0; } 80% { translate: 2px 0; } }
@keyframes authPop { from { transform: scale(0); } to { transform: scale(1); } }

.auth-root button:hover:not(:disabled) { opacity: 1; }
.auth-footer { position: relative; }
.auth-footer footer { padding-top: 14px !important; }
.auth-footer footer, .auth-footer footer span { color: rgba(214,236,232,.62) !important; }

/* 3.63.0 phones and tablets: the card (our logo + "Fax Portal", the tagline under it), then the system status only (owner: less text under the card) */
@media (max-width: 900px) {
  .auth-layout { grid-template-columns: minmax(0, 480px); gap: 18px; padding: 28px 16px 4px; }
  .auth-brand, .auth-hero { display: contents; }
  .auth-brand-top, .auth-lede, .auth-live, .auth-features, .auth-headline { display: none; }
  /* owner, Oct 22: on phones the logo, then "Fax Portal" under it, then a smaller tagline */
  .auth-card-logo { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
  .auth-card-logo img { height: 40px; }
  .auth-card-name { font-size: 26px; }
  .auth-card-tagline { display: block; margin: 0 0 20px; padding-bottom: 16px; font-size: 15px; letter-spacing: -.2px; font-weight: 700; }
  .auth-card { order: 2; }
  .auth-strip { order: 3; justify-content: center; font-size: 12.5px; border-top: none; padding-top: 0; }
  .auth-strip .auth-strip-extra { display: none; }
  .auth-scan { display: none; }
}
@media (max-width: 480px) {
  .auth-layout { padding: 18px 12px 0; gap: 14px; }
  .auth-card { padding: 26px 20px 22px; border-radius: 20px; }
}
@media (prefers-reduced-motion: reduce) {
  .auth-root *, .auth-root *::before, .auth-root *::after { animation: none !important; transition: none !important; }
}
`;
